import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NavBarLogo } from './NavBarLogo';
import { authHeaders, startViewingAs, useAdminSession } from './impersonation';
import type { AdminUserMatch } from '../../shared/src/types/admin';

const formatDate = (value: number | null) =>
  value
    ? new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

/** soulthread.me/admin: find someone and view SoulThread as they see it, read-only. */
export default function AdminPage() {
  const token = localStorage.getItem('soulthread.session') || '';
  const session = useAdminSession(token);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<AdminUserMatch[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!session?.isAdmin) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      fetch('/api/admin-console/users?query=' + encodeURIComponent(query), {
        headers: authHeaders(token),
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) throw new Error('People could not be loaded.');
          setUsers(((await response.json()) as { users: AdminUserMatch[] }).users);
          setError('');
        })
        .catch((failure: Error) => {
          if (failure.name !== 'AbortError') setError(failure.message);
        });
    }, 200);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query, session?.isAdmin, token]);

  return (
    <main className="sync-page admin-page">
      <header className="admin-header">
        <NavBarLogo />
        <Link to="/app">Back to your SoulThread</Link>
      </header>
      {!token ? (
        <p className="admin-note">
          <Link to="/app">Sign in</Link> to your staff account first.
        </p>
      ) : !session ? (
        <p className="admin-note">Checking access…</p>
      ) : !session.isAdmin ? (
        <p className="admin-note">This page is for SoulThread staff.</p>
      ) : (
        <section className="admin-panel">
          <h1>People</h1>
          <input
            className="admin-search"
            type="search"
            placeholder="Search by phone number"
            aria-label="Search by phone number"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoFocus
          />
          {error && (
            <p className="sync-error" role="alert">
              {error}
            </p>
          )}
          {users && users.length === 0 && <p className="admin-note">No one matches that.</p>}
          {users && users.length > 0 && (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Joined</th>
                  <th>Messages</th>
                  <th>Last message</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.phoneNumber}</td>
                    <td>{formatDate(user.joinedAt)}</td>
                    <td>{user.messageCount.toLocaleString()}</td>
                    <td>{formatDate(user.lastMessageAt)}</td>
                    <td>
                      <a
                        href={`/app?impersonate=${encodeURIComponent(user.id)}`}
                        onClick={() => startViewingAs(user.id)}
                      >
                        View as ↗
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </main>
  );
}
